import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, FormGroup, Grid, Paper, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { connect } from 'formik';
import { compose } from 'recompose';
import classNames from 'classnames';

import { ManualNotificationType, UserSegment } from 'api';
import { CustomSVG, Loaders, ValidatedTextField } from 'components';
import { useClient } from 'hooks';
import { FormikMessage } from 'features/admin/types';

import { MAX_MESSAGE_LENGTH } from './constants';
import SegmentSelect from './SegmentSelect';
import Schedule from './Schedule';
import EmailInput from './EmailInput';

interface IProps {
  titleI18nKey: string;
  working: boolean;
  segments: UserSegment[];
  onEdit?: () => void;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikMessage;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    divider: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    primaryPadding: {
      padding: `${theme.spacing(2)}px ${theme.spacing(8)}px ${theme.spacing(1)}px`,
    },
    formGroup: {
      marginTop: theme.spacing(2),
    },
    options: {
      marginTop: theme.spacing(3),
    },
    cancelButton: {
      paddingLeft: 0,
    },
    submitButton: {
      minWidth: 200,
    },
    safe: {
      color: theme.custom.palette.grey1,
    },
    warning: {
      color: theme.custom.palette.orange,
    },
    error: {
      color: theme.custom.palette.red,
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

const Form: FC<IPropsEnhanced> = ({ classes, formik, segments, onEdit, working, titleI18nKey }) => {
  const { title } = useClient();
  const { t } = useTranslation();

  const { message, notification_type } = formik.values;
  const isScheduled = notification_type === ManualNotificationType.Scheduled;
  const i18nKey = isScheduled ? 'common:schedule' : 'common:send';

  function renderCounter() {
    const { body } = message;
    const difference = MAX_MESSAGE_LENGTH - body.length;
    const className = classNames({
      [classes.safe]: difference >= 21,
      [classes.warning]: difference <= 20,
      [classes.error]: difference < 0,
    });

    return (
      <Typography className={className}>{`${
        !!body ? body.length : 0
      }/${MAX_MESSAGE_LENGTH}`}</Typography>
    );
  }

  function renderSubmit() {
    if (working) {
      return (
        <div className={classes.submitButton}>
          <Loaders.Standard height={38} />
        </div>
      );
    }

    return (
      <Button
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        className={classes.submitButton}>
        {t(i18nKey)}
      </Button>
    );
  }

  return (
    <Paper className={classes.root}>
      <Grid container alignItems="center" justify="space-between">
        <Grid item>
          <Typography variant="subtitle2">{t(titleI18nKey, { title })}</Typography>
        </Grid>
        <Grid item>{renderCounter()}</Grid>
      </Grid>
      <Divider className={classes.divider} />
      <SegmentSelect segments={segments} />
      <div className={classes.primaryPadding}>
        <ValidatedTextField
          persistLabel
          name="message.body"
          i18nKey="messages:addCustomNotification"
        />
        <ValidatedTextField persistLabel name="message.url" i18nKey="messages:addOptionalUrl" />
        <FormGroup className={classes.formGroup}>
          <Schedule />
          <EmailInput />
        </FormGroup>
      </div>
      <Divider className={classes.divider} />
      <Grid container justify="space-between">
        <Grid item xs>
          {onEdit && (
            <Button
              size="large"
              onClick={onEdit}
              className={classes.cancelButton}
              startIcon={<CustomSVG name="delete" />}>
              {t('common:cancel')}
            </Button>
          )}
        </Grid>
        <Grid item>{renderSubmit()}</Grid>
      </Grid>
    </Paper>
  );
};

export default enhancers(Form);
